import React from 'react';

import styled from 'styled-components';

import { Dictionary } from '@rbi-ctg/frontend';
import useShallowMemo from 'hooks/use-shallow-memo';
import { ITable } from 'utils/table';

interface ITableProps<Row extends Dictionary> {
  table: ITable<Partial<Row>>;
  columnOrder?: (keyof Row)[];
}

const TableTag = styled.table`
  width: 100%;
  table-layout: fixed;
  margin: 0;
  border-spacing: 0;
  border-collapse: collapse;

  td,
  th {
    white-space: nowrap;
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const HeadRowGroup = styled.thead`
  th {
    font-weight: ${Styles.fontWeight.heavy};
    padding-top: 1rem;
    padding-bottom: 1rem;

    ${Styles.mobile`
      &:first-child {
        width: 100px;
        text-align: left;
      }
    `}
  }

  tr:last-of-type th {
    border-bottom: 1px solid ${Styles.color.grey.four};
  }
`;

const BodyRowGroup = styled.tbody`
  td {
    padding-top: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid ${Styles.color.grey.four};

    ${Styles.mobile`
      &:first-child {
        white-space: normal;
        width: 100px;
      }
    `}
  }

  tr:last-of-type td {
    border-bottom: none;
  }
`;

const Table = <Row extends Dictionary>(props: ITableProps<Row>) => {
  /**
   * Make sure all column keys line up between head and body.
   */
  const columnOrder = useShallowMemo(() => {
    const orderedColumnKeys = props.columnOrder || [];
    const firstRow = props.table.head[0] || props.table.body[0] || {};
    Object.keys(firstRow).forEach(key => {
      if (!orderedColumnKeys.includes(key)) {
        orderedColumnKeys.push(key);
      }
    });

    return orderedColumnKeys;
  }, [props.columnOrder, props.table.head[0], props.table.body[0]]);

  return (
    <TableTag>
      <HeadRowGroup>
        {props.table.head.map((row, rowIndex) => (
          <tr key={`row.${rowIndex}`}>
            {columnOrder.map((key, keyIndex) => (
              <th key={`${key}.${keyIndex}`}>{row[key]}</th>
            ))}
          </tr>
        ))}
      </HeadRowGroup>
      <BodyRowGroup>
        {props.table.body.map((row, rowIndex) => (
          <tr key={`row.${rowIndex}`}>
            {columnOrder.map((key, keyIndex) => (
              <td key={`${key}.${keyIndex}`}>{row[key]}</td>
            ))}
          </tr>
        ))}
      </BodyRowGroup>
    </TableTag>
  );
};

export default Table;
